import React, { useEffect } from 'react';
import { CreateButton } from 'react-admin';
import { useLocation } from 'react-router-dom';
import {
  required,
  List,
  Datagrid,
  Edit,
  Create,
  SimpleForm,
  ImageField,
  TextField,
  TextInput,
  FileInput,
  FileField,
  EditButton,
  ReferenceInput,
  AutocompleteInput,
  ReferenceField,
  FunctionField,
  Filter,
  BooleanField,
  BooleanInput,
  TopToolbar,
  Show,
  TabbedShowLayout,
  Tab,
  ShowButton,
  useShowController,
  Loading,
  DateField,
} from 'react-admin';
import VideoJS from '../VideoJS';
import VideoDownloadButton from '../VideoDownloadButton';

const getStatus = (url) => {
  if (url === null) {
    return "Processing";
  }
  return "Finished";
}

const TextFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Title" source="title" alwaysOn />
    <TextInput label="CourseId" source="courseId" alwaysOn />
  </Filter>
);

const extractVideoId = (url) => {
  if (!url) return null;
  const videoId = url.split('/').reverse()[1];
  return videoId;
}

const EmptyPanel = ({ label }) => (
  <div
    width={500}
    px={2}
    py={5}
    borderRadius={5}
    border={1}
    borderColor="#dddddd"
    bgcolor="#f5f5f5"
    color="#aaaaaa"
    textAlign="center"
    fontSize={12}
  >
    {label}
  </div>
)

const VideoListActions = ({ basePath }) => (
  <TopToolbar>
    <CreateButton basePath={basePath} label="Видео нэмэх" />
  </TopToolbar>
);

export const VideoList = props => (
  <List {...props} filters={<TextFilter />} actions={<VideoListActions />}>
    <Datagrid optimized>
      <TextField label="ID" source="id" />
      <ReferenceField source="courseId" reference="skills/courses">
        <TextField label="Хичээлийн нэр" source="title" />
      </ReferenceField>
      <TextField label="Нэр" source="title" />
      <TextField label="Дараалал" source="order" />
      <TextField label="Хугацаа (секунд)" source="duration" />
      <FunctionField label="Статус" render={record => getStatus(record.url)} />
      <BooleanField label="Үнэгүй үзэх" source="isPreview" />
      <FunctionField label="Татаж авах" render={
        record => (record?.videoId || extractVideoId(record?.url)) ? (
          <VideoDownloadButton videoId={(record?.videoId || extractVideoId(record?.url))} />
        ) : (
          <>Видео байхгүй</>
        )
      } />
      <ShowButton />
      <EditButton />
    </Datagrid>
  </List>
);

const VideoTitle = ({ record }) => {
  const { id } = record;
  return <span>Video {record ? `"${id}"` : ''}</span>;
};

export const VideoEdit = props => {
  return (
    <Edit title={<VideoTitle />} {...props}>
      <SimpleForm>
        <TextInput disabled source="id" />
        <ReferenceInput fullWidth source="courseId" reference="skills/courses" validate={[required()]} perPage={500}>
          <AutocompleteInput optionText="title" />
        </ReferenceInput>
        <BooleanInput label="Үнэгүй үзэж болох эсэх" source="isPreview" />
        <TextInput fullWidth label="Нэр" source="title" validate={[required()]} />
        <TextInput multiline fullWidth rows={5} label="Танилцуулга" source="description" />
        <TextInput label="Хугацаа (секунд)" source="duration" />
        <DateField label="Үүсгэсэн огноо" source="createdAt" showTime />
        <ImageField source="thumbnailImage" label="Зураг" />
        <FileInput source="imgFile" label="Зураг" accept="image/*">
          <FileField source="img" title="image" />
        </FileInput>
        <FileInput source="file" label="Бичлэг" accept="video/mp4">
          <FileField source="video" title="video" />
        </FileInput>
        <TextInput label="Дараалал" source="order" pattern="[0-9]{1,3}" />
        <FunctionField
          label="Видео"
          render={record => (record?.videoId || extractVideoId(record?.url)) ? (
            <>
              <VideoJS videoId={(record?.videoId || extractVideoId(record?.url))} />
              <VideoDownloadButton videoId={(record?.videoId || extractVideoId(record?.url))} />
            </>
          ) : (
            <EmptyPanel label="Хов хоосон! 😥 😥 😥" />
          )}
        />
      </SimpleForm>
    </Edit>
  );
};

export const VideoCreate = props => {
  const location = useLocation();
  
  const searchParams = new URLSearchParams(location.search);
  const courseId = searchParams.get('courseId');
  
  return (
    <Create {...props}>
      <SimpleForm initialValues={{ courseId: courseId || undefined }}>
        <ReferenceInput fullWidth source="courseId" reference="skills/courses" validate={[required()]} perPage={500}>
          <AutocompleteInput optionText="title" />
        </ReferenceInput>
        <BooleanInput label="Үнэгүй үзэж болох эсэх" source="isPreview" />
        <TextInput fullWidth label="Нэр" source="title" validate={[required()]} />
        <TextInput multiline fullWidth rows={5} label="Танилцуулга" source="description" />
        <FileInput source="imgFile" label="Зураг" accept="image/*">
          <FileField source="img" title="image" />
        </FileInput>
        <FileInput source="file" label="Бичлэг" accept="video/mp4">
          <FileField source="video" title="video" />
        </FileInput>
        <TextInput label="Дараалал" source="order" pattern="[0-9]{1,3}" />
      </SimpleForm>
    </Create>
  );
};

export const VideoShow = props => {
  const { record, loading } = useShowController(props);
  
  useEffect(() => {
    if (record) {
      console.log('Video Show Detail Record:', record);
    }
  }, [record]);

  const ShowActions = ({ basePath, data, resource }) => (
    <TopToolbar>
      <EditButton basePath={basePath} record={data} />
    </TopToolbar>
  );
  
  if (loading) return <Loading />;
  
  return (
    <Show {...props} actions={<ShowActions />}>
      <TabbedShowLayout>
        <Tab label="Видеоны мэдээлэл">
          <TextField source="id" />
          <ReferenceField source="courseId" reference="skills/courses">
            <TextField label="Хичээлийн нэр" source="title" />
          </ReferenceField>
          <TextField label="Нэр" source="title" />
          <TextField label="Дараалал" source="order" />
          <TextField label="Танилцуулга" source="description" />
          <BooleanField label="Үнэгүй үзэх" source="isPreview" />
          <FunctionField label="Статус" render={record => getStatus(record.url)} />
          <TextField label="Хугацаа (секунд)" source="duration" />
          <DateField label="Үүсгэсэн огноо" source="createdAt" showTime />
          <ImageField source="thumbnailImage" label="Зураг" />
          <FunctionField
            label="Видео"
            render={record => (record?.videoId || extractVideoId(record?.url)) ? (
              <>
                <VideoJS videoId={(record?.videoId || extractVideoId(record?.url))} />
                <VideoDownloadButton videoId={(record?.videoId || extractVideoId(record?.url))} />
              </>
            ) : (
              <EmptyPanel label="Хов хоосон! 😥 😥 😥" />
            )}
          />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
